// Fonctions métier, compétences et niveaux : libellés et aides pures (testées dans skills.test.js).
// Le serveur reste la seule autorité : ces fonctions ne font que guider l'interface.

// Les quatre niveaux sont fixes et ordonnés ; seuls leurs libellés sont modifiables (servis par /skill-levels).
export const LEVEL_CODES = ["discovery", "autonomous", "trainer", "expert"];
export const DEFAULT_LEVEL_LABELS = { discovery: "Découverte", autonomous: "Autonome", trainer: "Formateur", expert: "Expert" };

export const SOURCE_LABELS = { declared: "Déclaré", assessed: "Évalué" };

export function levelRank(code) {
  return LEVEL_CODES.indexOf(code) + 1;
}

// Niveau attendu par compétence pour les fonctions d'une fiche : le plus exigeant l'emporte quand deux fonctions attendent la même.
export function expectedSkills(functions, functionIds) {
  const expected = new Map();
  for (const fn of functions) {
    if (!(functionIds || []).includes(fn.id)) continue;
    for (const item of fn.expected_skills || []) {
      const known = expected.get(item.skill_id);
      if (!known || levelRank(item.level) > levelRank(known.level)) {
        expected.set(item.skill_id, { level: item.level, functions: [...(known?.functions || []), fn.label] });
      } else {
        known.functions.push(fn.label);
      }
    }
  }
  return expected;
}

// Une ligne par compétence connue de la fiche (saisie ou attendue) : niveau actuel, niveau attendu, écart.
export function skillRows(entries, expected) {
  const rows = new Map();
  for (const entry of entries) rows.set(entry.skill_id, { skill_id: entry.skill_id, entry, expected: null });
  for (const [skillId, want] of expected) {
    rows.set(skillId, { skill_id: skillId, entry: rows.get(skillId)?.entry || null, expected: want });
  }
  return [...rows.values()].map((row) => ({
    ...row,
    gap: !!row.expected && (!row.entry || levelRank(row.entry.level) < levelRank(row.expected.level)),
  }));
}

// Rôles suggérés par les fonctions d'une fiche (une simple indication, jamais appliquée automatiquement).
// Un rôle par entrée, avec les fonctions qui le suggèrent. `functions` vient de /job-functions : sans le droit de gérer le
// référentiel, le serveur n'y met pas de rôle suggéré et la liste est vide.
export function suggestedRoles(functions, functionIds) {
  const byRole = new Map();
  for (const fn of functions || []) {
    if (!(functionIds || []).includes(fn.id) || !fn.suggested_role) continue;
    const item = byRole.get(fn.suggested_role) || { role: fn.suggested_role, label: fn.suggested_role_label || fn.suggested_role, functions: [] };
    item.functions.push(fn.label);
    byRole.set(fn.suggested_role, item);
  }
  return [...byRole.values()];
}

// Corps d'un PATCH de fonction métier : uniquement ce qui a changé.
export function buildFunctionPatch(original, draft) {
  const patch = { expected_version: original.version };
  if (draft.label.trim() !== original.label) patch.label = draft.label.trim();
  if ((draft.description || "").trim() !== (original.description || "")) patch.description = draft.description.trim() || null;
  if ((draft.suggested_role || null) !== (original.suggested_role || null)) patch.suggested_role = draft.suggested_role || null;
  const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  if (!same(draft.expected_skills, original.expected_skills || [])) patch.expected_skills = draft.expected_skills;
  return patch;
}

// Corps d'un PATCH de compétence : uniquement ce qui a changé ; un texte vidé est envoyé à null.
export function buildSkillPatch(original, draft, { withFeatures = false } = {}) {
  const patch = { expected_version: original.version };
  if (draft.label.trim() !== original.label) patch.label = draft.label.trim();
  for (const key of ["description", "criteria", "domain"]) {
    if ((draft[key] || "").trim() !== (original[key] || "")) patch[key] = (draft[key] || "").trim() || null;
  }
  // Only someone who could see the catalogue's choices sends them (otherwise the skill keeps its features untouched).
  const features = draft.feature_codes || [];
  if (withFeatures && features.join(",") !== (original.feature_codes || []).join(",")) patch.feature_codes = features;
  return patch;
}

export function isExpectedSkillsValid(items) {
  const ids = items.map((i) => i.skill_id);
  return ids.every(Boolean) && new Set(ids).size === ids.length && items.every((i) => LEVEL_CODES.includes(i.level));
}

// Habilitations : l'état est calculé par le serveur à partir des dates (active, à venir, expirée) ou de la révocation.
export const ACCREDITATION_STATE_LABELS = { active: "Valide", upcoming: "À venir", expired: "Expirée", revoked: "Révoquée" };
export const ACCREDITATION_STATE_CLASSES = {
  active: "bg-emerald-50 text-emerald-700",
  upcoming: "bg-sky-50 text-sky-700",
  expired: "bg-slate-100 text-slate-600",
  revoked: "bg-slate-200 text-slate-600",
};

// Valides et à venir d'abord ; expirées et révoquées restent consultables, repliées.
export function splitAccreditations(items) {
  const current = items.filter((a) => a.state === "active" || a.state === "upcoming");
  return { current, past: items.filter((a) => !current.includes(a)) };
}

export function isGrantFormValid(form) {
  if (!form.skill_id || !LEVEL_CODES.includes(form.level) || !form.valid_from) return false;
  return !form.valid_until || form.valid_until >= form.valid_from;
}

export const MIN_REVOKE_REASON = 3;

// Paramètres de l'annuaire : niveau et date d'habilitation n'ont de sens qu'avec une compétence.
export function skillFilterParams({ jobFunctionId, skillId, minLevel, accreditedOn }) {
  const params = {};
  if (jobFunctionId) params.job_function_id = jobFunctionId;
  if (skillId) {
    params.skill_id = skillId;
    if (minLevel) params.min_level = minLevel;
    if (accreditedOn) params.accredited_on = accreditedOn;
  }
  return params;
}
